// The activation's own record: name, hall, clock, run, and when the hall is open.
//
// ---------------------------------------------------------------------------
// THIS IS THE WRITE. THE KIOSK PAGE IS THE READ.
//
// A schedule is judged against these facts. Putting the editors on the kiosk
// page would make the refusals internally consistent and about whatever week
// somebody typed next to the rotation, which is the failure Kiosk.tsx's header
// is written to prevent. So the dates and the hours live here, the kiosk page
// re-reads the directory, and `groundFor` derives what the schedule is judged
// against from the row rather than from a second copy.
//
// ---------------------------------------------------------------------------
// THE SERVICE ALREADY ACCEPTS THE WRITE, AND THIS FILE DOES NOT INVENT ONE
//
// `POST /v1/admin/activation-amendments` is requireAmender: a nova_admin
// session that already proved a second factor at login. No authenticator code
// travels on this request, and no `amended_by` is sent — the session is the
// record. A client viewer following a deep link still gets this screen; the
// service answers 403. Hiding the tab is not refusing the route, which is the
// argument App.tsx already makes for the export and for enrolment.
//
// ---------------------------------------------------------------------------
// A FACT THAT HAS BEEN SAID CANNOT BE TAKEN BACK
//
// 109 refuses null on a field that is already set, and refuses an empty
// opening-hours array. This form therefore omits a cleared box rather than
// sending "" or null, and it will not POST a half run or a single hour. Those
// two are caught here because the service would refuse them after a trip, and
// the person would read that as a fault in what they typed.
//
// BLANK TIMES ON AN UNSAID HALL ARE ALL DAY. 108's three states stay: no row
// is unsaid, a row with both times is a window, a row with nulls is shut. This
// form does not treat unsaid as open on the read path. Saving with both time
// boxes empty, a known run, and hours still unsaid writes 00:00–23:59 for
// every day of the run. Clearing a field on a hall that already has hours
// still keeps those hours.

import { DefList, NoticeBox, PageHead } from "@nova/desk-ui/parts";
import { FieldRow, Info, SecLabel, Seg } from "@nova/desk-ui/kit";
import { Loading } from "@nova/desk-ui/states";
import { useEffect, useState, type ReactNode } from "react";
import type { ActivationLine, OpeningHours } from "../../../operator/src/activations.js";
import { amendActivation, closeActivation, pauseActivation, type AdminRefusal } from "../api/admin.js";
import type { ApiOptions } from "../api/client.js";
import { showDays } from "../kiosk/admin.js";
import { groundFor } from "../kiosk/ground.js";
import type { SessionToken } from "../session/store.js";
import { ActivationUuid } from "./ActivationUuid.js";
import { AdminRefused } from "./Authorise.js";
import { Refusal } from "./Refusal.js";
import { lifecycle, STATE_MEANS, STATE_TAG } from "./lifecycle.js";
import { moment } from "../operator/present.js";
import { opens } from "./roleAccess.js";
import { useShow } from "./Show.js";
import { TimeZoneField } from "./TimeZoneField.js";

/**
 * What a pause does not stop, said where the button is (Gabe, 13 Oct): the
 * honest edges of 151, so nobody reads Paused as more than it is.
 */
export const PAUSE_DOES_NOT_REACH =
  "A pause stops what the service takes itself: registrations, passcodes, wheel spins, photo booth sessions, " +
  "RSVPs, check-ins and screen pulls. It does not reach the tablets enrolled on the stand, which keep capturing " +
  "and sync as usual, or Ride to Survive, whose plays run on the game's own service.";

export type ShowDetailsProps = {
  readonly api: ApiOptions;
  readonly token: SessionToken;
  readonly activationId: string;
  readonly onSessionEnded: (detail: string) => void;
  readonly role: string;
  readonly onEnrol: () => void;
};

export function ShowDetails({
  api,
  token,
  activationId,
  onSessionEnded,
  role,
  onEnrol,
}: ShowDetailsProps): ReactNode {
  const show = useShow();

  const [name, setName] = useState("");
  const [venue, setVenue] = useState("");
  const [timeZone, setTimeZone] = useState("");
  const [territory, setTerritory] = useState<"UK" | "US">("UK");
  const [opensOn, setOpensOn] = useState("");
  const [closesOn, setClosesOn] = useState("");
  const [opensAt, setOpensAt] = useState("");
  const [closesAt, setClosesAt] = useState("");
  const [hoursVary, setHoursVary] = useState(false);

  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState(false);
  const [refusal, setRefusal] = useState<AdminRefusal | null>(null);
  const [runNote, setRunNote] = useState<string | null>(null);
  const [hoursNote, setHoursNote] = useState<string | null>(null);
  const [changeNote, setChangeNote] = useState<string | null>(null);
  // Separate from changeNote, which belongs to the form below. A refusal about
  // the show's state has to appear beside the button that asked for it, not at
  // the top of a panel about the hall's opening hours.
  const [closureNote, setClosureNote] = useState<string | null>(null);

  const activation: ActivationLine | null = show.status === "ready" ? show.line : null;

  useEffect(() => {
    if (activation === null) return;
    setName(said(activation.name));
    setVenue(said(activation.venue));
    setTimeZone(said(activation.time_zone));
    setTerritory(activation.territory === "US" ? "US" : "UK");
    setOpensOn(said(activation.opens_on));
    setClosesOn(said(activation.closes_on));
    const hours = groundFor(activation).hours;
    if (hours.kind === "one_window") {
      setOpensAt(hours.opensAt);
      setClosesAt(hours.closesAt);
      setHoursVary(false);
      return;
    }
    setOpensAt("");
    setClosesAt("");
    setHoursVary(hours.kind === "varies");
  }, [activation]);

  if (show.status === "idle" || show.status === "reading") {
    return (
      <div className="stack">
        <header className="head">
          <h1>Show details</h1>
        </header>
        <Loading rows={4} cols={2} label="Reading show details…" />
      </div>
    );
  }

  if (show.status === "failed") {
    return (
      <Refusal
        onRetry={show.reload}
        what="Could not load show details"
        failure={show.failure}
        role={role}
        onEnrol={onEnrol}
        notCounted="No changes were made."
      />
    );
  }

  if (activation === null) {
    return (
      <div className="stack">
        <header className="head">
          <h1>Activation not found</h1>
          <p className="lede">Check the link or ask Nova to grant this account access.</p>
        </header>
        <ActivationUuid id={activationId} />
      </div>
    );
  }

  const clientId = activation.client.client_id;

  const submit = async (): Promise<void> => {
    setRefusal(null);
    setRunNote(null);
    setHoursNote(null);
    setChangeNote(null);
    setSaved(false);

    if (clientId === null || clientId === "") {
      setChangeNote("This show is not linked to a client, so it cannot be saved.");
      return;
    }

    const nextName = stated(name);
    const nextVenue = stated(venue);
    const nextZone = stated(timeZone);
    const nextFirst = stated(opensOn);
    const nextLast = stated(closesOn);
    const nextOpen = asClock(opensAt);
    const nextClose = asClock(closesAt);

    if ((nextFirst === undefined) !== (nextLast === undefined)) {
      setRunNote("Enter both the first and last day.");
      return;
    }
    if (nextFirst !== undefined && nextLast !== undefined && nextLast < nextFirst) {
      setRunNote("The last day must be on or after the first day.");
      return;
    }

    const openSaid = nextOpen !== "";
    const closeSaid = nextClose !== "";
    if (openSaid !== closeSaid) {
      setHoursNote("Enter both the opening and closing time.");
      return;
    }

    const first = nextFirst ?? said(activation.opens_on);
    const last = nextLast ?? said(activation.closes_on);
    let openingHours: readonly { on_date: string; opens_at: string; closes_at: string }[] | undefined;
    if (openSaid && closeSaid) {
      const hours = windowsForRun(first, last, nextOpen, nextClose);
      if (hours === "need_dates") {
        setHoursNote("Set the show dates before adding opening hours.");
        return;
      }
      if (hours === "bad_dates") {
        setHoursNote("Check the show dates.");
        return;
      }
      if (hours === "overran") {
        setHoursNote("This date range is too long for kiosk scheduling. Shorten it.");
        return;
      }
      if (!sameWindows(activation.opening_hours, hours)) openingHours = hours;
    } else if (groundFor(activation).hours.kind === "unsaid" && first !== "" && last !== "") {
      const hours = windowsForRun(first, last, ALL_DAY_OPENS, ALL_DAY_CLOSES);
      if (hours === "bad_dates") {
        setHoursNote("Check the show dates.");
        return;
      }
      if (hours === "overran") {
        setHoursNote("This date range is too long for kiosk scheduling. Shorten it.");
        return;
      }
      if (hours !== "need_dates" && !sameWindows(activation.opening_hours, hours)) openingHours = hours;
    }

    const input = {
      activationId,
      clientId,
      ...(nextName !== undefined && nextName !== said(activation.name) ? { name: nextName } : {}),
      ...(nextVenue !== undefined && nextVenue !== said(activation.venue) ? { venue: nextVenue } : {}),
      ...(nextZone !== undefined && nextZone !== said(activation.time_zone) ? { timeZone: nextZone } : {}),
      ...(territory !== (activation.territory ?? "UK") ? { territory } : {}),
      ...(nextFirst !== undefined &&
      nextLast !== undefined &&
      (nextFirst !== said(activation.opens_on) || nextLast !== said(activation.closes_on))
        ? { opensOn: nextFirst, closesOn: nextLast }
        : {}),
      ...(openingHours === undefined ? {} : { openingHours }),
    };

    if (
      input.name === undefined &&
      input.venue === undefined &&
      input.timeZone === undefined &&
      input.territory === undefined &&
      input.opensOn === undefined &&
      input.openingHours === undefined
    ) {
      setChangeNote("Nothing here has changed.");
      return;
    }

    setBusy(true);
    const result = await amendActivation(api, token, input);
    setBusy(false);

    if (!result.ok) {
      setRefusal(result);
      if (result.kind === "session_ended") onSessionEnded(result.detail);
      return;
    }

    setSaved(true);
    show.reload();
  };

  const state = lifecycle(activation);

  /**
   * Record that the show has finished, or take that back.
   *
   * Shares `busy` and `refusal` with the amendment above rather than keeping its
   * own pair. Both write to the same row, so two of them in flight at once is a
   * state nobody should be able to reach by pressing two buttons quickly, and
   * one flag is what makes that true rather than a comment asking for it.
   */
  const changeClosure = async (next: boolean): Promise<void> => {
    setRefusal(null);
    setSaved(false);
    setClosureNote(null);

    if (clientId === null || clientId === "") {
      setClosureNote("This show is not linked to a client, so its state cannot be changed.");
      return;
    }

    setBusy(true);
    const result = await closeActivation(api, token, {
      activationId,
      clientId,
      closed: next,
    });
    setBusy(false);

    if (!result.ok) {
      setRefusal(result);
      if (result.kind === "session_ended") onSessionEnded(result.detail);
      return;
    }

    // Re-read rather than assuming. The row this screen draws is the one the
    // service holds, and a screen that patches its own copy is a screen that
    // can disagree with the Activations list about the same show.
    show.reload();
  };

  // Recorded as finished, read off the column rather than off the state: a
  // paused show can also be closed, and the close button has to say which.
  const isClosed = said(activation.closed_at) !== "";
  const pausedAt = said(activation.paused_at);

  /**
   * Pause the show, or resume it (151, decision C3). The closure's shape and
   * the same shared `busy`, for the same reason: both write the one row.
   */
  const changePause = async (next: boolean): Promise<void> => {
    setRefusal(null);
    setSaved(false);
    setClosureNote(null);

    if (clientId === null || clientId === "") {
      setClosureNote("This show is not linked to a client, so its state cannot be changed.");
      return;
    }

    setBusy(true);
    const result = await pauseActivation(api, token, { activationId, clientId, paused: next });
    setBusy(false);

    if (!result.ok) {
      setRefusal(result);
      if (result.kind === "session_ended") onSessionEnded(result.detail);
      return;
    }
    show.reload();
  };

  return (
    <div className="stack">
      <PageHead
        title="Show details"
        lede="Set the event name, venue, dates, time zone and opening hours."
      />

      {saved ? (
        <NoticeBox kind="good" title="Show details saved">
          Kiosk schedule checks now use these details.
        </NoticeBox>
      ) : null}

      {refusal === null ? null : <AdminRefused refusal={refusal} />}

      <section>
        <SecLabel>Event record</SecLabel>
        <ActivationUuid
          id={activationId}
          help="The identifier in the address. It cannot be changed."
        />
      </section>

      <section>
        <SecLabel>State</SecLabel>
        <div className="panel panel-stack">
          {/* ONE OF THE THREE IS NOW A WRITE, AND THE OTHER TWO STILL ARE NOT.
              The prototype puts three radios here, active, paused and archived,
              and Nova has no such field. An activation carries closed_at and
              sealed_at. Closing is reversible and is offered below; sealing is
              the decision that a record is final, 106 refuses the publishing
              role the privilege to write it at all, and nothing on this console
              reaches it. Drawing a control for a thing that could only ever
              fail is the argument Export.tsx makes about a tab that always
              refuses, so the seal is described rather than offered. */}
          <p>
            One of four, and it is what the Activations list files this show under. It is not a
            delete: nothing here removes an activation, its data or its content.
          </p>
          {/* One row each, which is what .dl pads. Bare dt/dd children sit in
              the 1px grid with no inset, so Now and Which means read as text
              pasted on the card border. DefList is the shared wrapper. */}
          {/* What sealing is sits behind an (i) beside the state, as the October
              hand-off draws it on its Sealed row: the two lines that used to
              be under the buttons, final and permanent and managed elsewhere,
              joined into the one (i). */}
          <DefList
            rows={[
              [
                "Now",
                <span>
                  <span className={`tag ${STATE_TAG[state]}`}>{state}</span>
                  <Info label="About sealing">
                    A sealed event is final and cannot be reopened. Sealing is permanent and is managed
                    elsewhere. Data export requires a sealed event.
                  </Info>
                </span>,
              ],
              ["Which means", STATE_MEANS[state]],
              ...(pausedAt === "" ? [] : ([["Paused since", moment(pausedAt)]] as [string, ReactNode][])),
            ]}
          />

          {/* PAUSE AND RESUME (C3, 12 Oct). Offered to whoever roleAccess.ts
              gives `pauseShow`, which is who the service admits: an Admin, and
              an Editor for a client they hold. Gone on a sealed show, which
              takes nothing already and does not come back. */}
          {state === "Sealed" || !opens({ role, canExport: null }, "pauseShow") ? null : (
            <div className="acts acts-flush">
              <button
                type="button"
                className="btn btn-quiet"
                disabled={busy}
                onClick={() => void changePause(state !== "Paused")}
              >
                {state === "Paused" ? "Resume this show" : "Pause this show"}
              </button>
              <Info label="What a pause does not reach">
                {PAUSE_DOES_NOT_REACH}
              </Info>
              <p className="help help-tight">
                {state === "Paused"
                  ? "Resuming lets registration, passcodes, plays, check-ins and the screens run again. Nothing was lost while it was paused."
                  : "Pausing stops registration, passcodes, plays, check-ins and screen pulls, and the screens say the stand is paused. Nothing is deleted, and it can be resumed."}
              </p>
            </div>
          )}

          {/* SEALED IS THE END OF THE ROAD AND THE BUTTON GOES AWAY, rather than
              being drawn disabled. A disabled control says "not yet"; this one
              is never coming back, and the (i) beside the state is the honest
              version of that. */}
          {state === "Sealed" ? null : (
            <div className="acts acts-flush">
              <button
                type="button"
                className="btn btn-quiet"
                disabled={busy}
                onClick={() => void changeClosure(!isClosed)}
              >
                {isClosed ? "Reopen this show" : "Close this show"}
              </button>
              <p className="help help-tight">
                {isClosed
                  ? "Reopening changes the event record. It does not restart any devices."
                  : "Closing marks the event as ended. It does not stop tablets, lock schedules or seal data."}
              </p>
            </div>
          )}

          {closureNote === null ? null : (
            <p className="help help-tight console-run-note" role="alert">
              {closureNote}
            </p>
          )}
        </div>
      </section>

      <section>
        <SecLabel>Show information</SecLabel>
        <div className="panel panel-stack">
          <div className="pair-wide">
            <label className="field">
              <span className="label">What the show is called</span>
              <input
                className="input"
                value={name}
                onChange={(event) => setName(event.target.value)}
                disabled={busy}
              />
            </label>

            <label className="field">
              <span className="label">Where it is held</span>
              <input
                className="input"
                value={venue}
                onChange={(event) => setVenue(event.target.value)}
                disabled={busy}
              />
            </label>
          </div>

          <div className="pair-wide">
            <TimeZoneField value={timeZone} onChange={setTimeZone} disabled={busy} />

            <div className="field">
              <span className="label">
                Territory
                <Info label="About Territory">
                  Sets the starting legal pack. You can still change it on Legal copy.
                </Info>
              </span>
              {/* Two buttons rather than two radios, which is the design's and is
                  also the more honest control: this is a choice between two named
                  things where one is always on, and a radio group implies a list
                  that could grow. Nothing was lost — Seg2 carries the group role
                  and the accessible name, and aria-pressed says which is on. */}
              <Seg
                label="Territory"
                value={territory}
                options={["UK", "US"] as const}
                onPick={busy ? () => {} : setTerritory}
              />
            </div>
          </div>

          <div className="pair-wide">
            <label className="field">
              <span className="label">The first day</span>
              <input
                className="input"
                type="date"
                value={opensOn}
                onChange={(event) => {
                  setOpensOn(event.target.value);
                  setRunNote(null);
                }}
                disabled={busy}
              />
            </label>

            <FieldRow label="The last day" help="Enter both dates. Use the same date twice for a one-day show.">
              <input
                className="input"
                type="date"
                value={closesOn}
                onChange={(event) => {
                  setClosesOn(event.target.value);
                  setRunNote(null);
                }}
                disabled={busy}
              />
            </FieldRow>
          </div>
          {runNote === null ? null : (
            <p className="help help-tight console-run-note" role="alert">
              {runNote}
            </p>
          )}

          <div className="pair-wide">
            <label className="field">
              <span className="label">The hall opens</span>
              <input
                className="input"
                type="time"
                value={opensAt}
                onChange={(event) => {
                  setOpensAt(event.target.value);
                  setHoursNote(null);
                }}
                disabled={busy}
              />
            </label>

            {/* One paragraph, as the design writes it: what blank means, what the
                window covers, and what clearing a field does. Behind the (i) on
                The hall closes since the October hand-off, which puts it there
                for the pair. */}
            <FieldRow
              label="The hall closes"
              help="Leave both blank to keep the hall open all day. This window applies to every day of the show. Clearing a field keeps its current value."
            >
              <input
                className="input"
                type="time"
                value={closesAt}
                onChange={(event) => {
                  setClosesAt(event.target.value);
                  setHoursNote(null);
                }}
                disabled={busy}
              />
            </FieldRow>
          </div>
          {hoursVary ? (
            <p className="help help-tight">
              Current hours vary by day. Saving here replaces them with one daily window.
            </p>
          ) : null}
          {hoursNote === null ? null : (
            <p className="help help-tight console-run-note" role="alert">
              {hoursNote}
            </p>
          )}

          {changeNote === null ? null : (
            <p className="help help-tight console-run-note" role="alert">
              {changeNote}
            </p>
          )}

          <div className="acts acts-flush">
            <button type="button" className="btn" disabled={busy} onClick={() => void submit()}>
              {busy ? "Saving…" : "Save show details"}
            </button>
          </div>
        </div>
      </section>
    </div>
  );
}

const ALL_DAY_OPENS = "00:00";
const ALL_DAY_CLOSES = "23:59";

const said = (value: string | null | undefined): string => (typeof value === "string" ? value.trim() : "");

const stated = (value: string): string | undefined => {
  const next = value.trim();
  return next === "" ? undefined : next;
};

/**
 * HTML `type="time"` may hand back HH:MM:SS. The service wants HH:MM.
 */
function asClock(value: string): string {
  const trimmed = value.trim();
  const match = /^(\d{2}:\d{2})(?::\d{2})?$/.exec(trimmed);
  return match === null ? trimmed : match[1]!;
}

type RunWindows =
  | "need_dates"
  | "bad_dates"
  | "overran"
  | readonly { on_date: string; opens_at: string; closes_at: string }[];

function windowsForRun(first: string, last: string, opens: string, closes: string): RunWindows {
  if (first === "" || last === "") return "need_dates";
  const { days, overran } = showDays(first, last);
  if (days.length === 0) return "bad_dates";
  if (overran) return "overran";
  return days.map((on_date) => ({ on_date, opens_at: opens, closes_at: closes }));
}

function sameWindows(
  stored: readonly OpeningHours[],
  next: readonly { on_date: string; opens_at: string; closes_at: string }[],
): boolean {
  for (const row of next) {
    const have = stored.filter((entry) => entry.on_date === row.on_date);
    if (have.length !== 1) return false;
    const only = have[0]!;
    if (only.opens_at !== row.opens_at || only.closes_at !== row.closes_at) return false;
  }
  return next.length > 0;
}
